/**
 * La semana de un profesional, vista como es.
 *
 * POR QUE NO UNA TABLA
 * ====================
 * Antes la agenda era una tabla: una fila por día de la semana, con hora de
 * inicio, hora de fin, duración del cupo y concurrencia. Correcta como
 * espejo del modelo, e inútil para la pregunta que uno tiene delante, que es
 * "¿cómo me queda la semana?". Para responderla había que leer veinte filas y
 * reconstruirla en la cabeza.
 *
 * Aquí se ve. Cada franja es un bloque colocado en su día y a su hora, y un
 * hueco o un solapamiento salta a la vista sin leer nada.
 *
 * POR QUE SIN LIBRERIA DE CALENDARIO
 * ==================================
 * Esto es una rejilla: siete columnas de días y una fila por media hora. Una
 * librería de calendario trae arrastrar y soltar, recurrencias, vistas de mes
 * y zonas horarias que aquí no hacen falta, y pesa más de 200 KB. `date-fns`
 * ya está en el proyecto y basta.
 *
 * SE PINTA EN HORA DE PARED
 * =========================
 * Una franja no tiene día: es "los martes de 8 a 12". No se convierte a fecha
 * ni a UTC en ningún momento, porque convertir abriría la puerta a errores de
 * zona horaria donde no hay ninguna.
 */

import { useMemo } from 'react'

export interface FranjaDeLaSemana {
  readonly id: string
  /** 0 es domingo, como en el resto del sistema. */
  readonly dayOfWeek: number
  readonly startTime: string
  readonly endTime: string
  readonly slotDurationMinutes: number
  readonly maxConcurrent: number
  readonly etiqueta?: string
  readonly isActive: boolean
}

interface Props {
  franjas: readonly FranjaDeLaSemana[]
  /** Clic en una franja existente. */
  onAbrirFranja?: (franja: FranjaDeLaSemana) => void
  /** Clic en un hueco: el día y la hora donde se pulsó. */
  onHueco?: (dia: number, hora: string) => void
}

/** Lunes primero: es como se lee una semana de trabajo. Domingo al final. */
const DIAS = [
  { numero: 1, nombre: 'Lunes', corto: 'Lun' },
  { numero: 2, nombre: 'Martes', corto: 'Mar' },
  { numero: 3, nombre: 'Miércoles', corto: 'Mié' },
  { numero: 4, nombre: 'Jueves', corto: 'Jue' },
  { numero: 5, nombre: 'Viernes', corto: 'Vie' },
  { numero: 6, nombre: 'Sábado', corto: 'Sáb' },
  { numero: 0, nombre: 'Domingo', corto: 'Dom' },
] as const

const aMinutos = (hora: string): number => {
  const [h, m] = hora.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

const aHora = (minutos: number): string =>
  `${String(Math.floor(minutos / 60)).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`

/** Alto de media hora, en pixeles. Con menos, media hora no se puede pulsar. */
const ALTO_MEDIA_HORA = 22

export function SemanaDeAgenda({ franjas, onAbrirFranja, onHueco }: Props) {
  /**
   * Las horas que se pintan.
   *
   * No se pinta el día entero: una rejilla de 00:00 a 24:00 son 48 filas de
   * las que 30 están siempre vacías, y obliga a desplazarse para ver lo que
   * importa. Se acota a lo que esta institución usa de verdad, con una hora
   * de margen a cada lado para que se vea que hay sitio antes y después.
   */
  const { desde, hasta } = useMemo(() => {
    const activas = franjas.filter((f) => f.isActive)
    if (activas.length === 0) return { desde: 7 * 60, hasta: 19 * 60 }

    const inicio = Math.min(...activas.map((f) => aMinutos(f.startTime)))
    const fin = Math.max(...activas.map((f) => aMinutos(f.endTime)))

    return {
      desde: Math.max(0, Math.floor((inicio - 60) / 60) * 60),
      hasta: Math.min(24 * 60, Math.ceil((fin + 60) / 60) * 60),
    }
  }, [franjas])

  const filas = (hasta - desde) / 30

  const porDia = useMemo(() => {
    const mapa = new Map<number, FranjaDeLaSemana[]>()
    for (const dia of DIAS) mapa.set(dia.numero, [])
    for (const f of franjas) {
      if (!f.isActive) continue
      mapa.get(f.dayOfWeek)?.push(f)
    }
    return mapa
  }, [franjas])

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[680px]">
        {/* Cabecera de días */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-[var(--border)]">
          <div />
          {DIAS.map((dia) => (
            <div
              key={dia.numero}
              className="px-2 py-2 text-center text-sm font-medium"
              title={dia.nombre}
            >
              <span className="hidden sm:inline">{dia.nombre}</span>
              <span className="sm:hidden">{dia.corto}</span>
            </div>
          ))}
        </div>

        {/* Rejilla */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)]">
          {/* Columna de horas */}
          <div>
            {Array.from({ length: filas }, (_, i) => {
              const minutos = desde + i * 30
              const enPunto = minutos % 60 === 0
              return (
                <div
                  key={minutos}
                  style={{ height: ALTO_MEDIA_HORA }}
                  className="pr-2 text-right text-xs text-[var(--foreground-muted)]"
                >
                  {enPunto ? aHora(minutos) : ''}
                </div>
              )
            })}
          </div>

          {/* Una columna por día */}
          {DIAS.map((dia) => (
            <div
              key={dia.numero}
              className="relative border-l border-[var(--border)]"
              style={{ height: filas * ALTO_MEDIA_HORA }}
            >
              {/* Las medias horas vacías: pulsables para crear */}
              {Array.from({ length: filas }, (_, i) => {
                const minutos = desde + i * 30
                return (
                  <button
                    key={minutos}
                    type="button"
                    aria-label={`${dia.nombre} a las ${aHora(minutos)}`}
                    onClick={() => onHueco?.(dia.numero, aHora(minutos))}
                    style={{ height: ALTO_MEDIA_HORA }}
                    className={`block w-full border-b transition-colors hover:bg-[var(--background-secondary)] ${
                      minutos % 60 === 0
                        ? 'border-[var(--border)]'
                        : 'border-[var(--border)] border-dashed opacity-60'
                    }`}
                  />
                )
              })}

              {/* Las franjas, encima */}
              {(porDia.get(dia.numero) ?? []).map((franja) => {
                const arriba = ((aMinutos(franja.startTime) - desde) / 30) * ALTO_MEDIA_HORA
                const alto =
                  ((aMinutos(franja.endTime) - aMinutos(franja.startTime)) / 30) * ALTO_MEDIA_HORA

                return (
                  <button
                    key={franja.id}
                    type="button"
                    onClick={() => onAbrirFranja?.(franja)}
                    style={{ top: arriba, height: Math.max(alto, ALTO_MEDIA_HORA) }}
                    // `flex-col justify-start` no es decoracion: un <button>
                    // centra su contenido verticalmente, y en un bloque de
                    // cuatro horas la etiqueta quedaba flotando a la mitad,
                    // como si la atencion empezara a las 10. Va arriba, a la
                    // altura de su hora de inicio. La barra de la izquierda da
                    // el borde de color que el relleno al 15% no alcanza a dar.
                    className="absolute inset-x-1 flex flex-col justify-start overflow-hidden rounded-md border border-[var(--primary)]/40 border-l-4 border-l-[var(--primary)] bg-[var(--primary)]/15 px-1.5 py-1 text-left text-xs transition-colors hover:bg-[var(--primary)]/25"
                    title={`${franja.etiqueta ?? ''} ${franja.startTime}-${franja.endTime}, cupos de ${franja.slotDurationMinutes} min`}
                  >
                    <span className="block font-semibold text-[var(--primary)]">
                      {franja.startTime}–{franja.endTime}
                    </span>
                    {/* La duración del cupo y la concurrencia solo caben si el
                        bloque es alto. En uno de media hora sobran: están en
                        el título y en el panel de la franja. */}
                    {alto >= ALTO_MEDIA_HORA * 3 && (
                      <span className="block text-[var(--foreground-muted)]">
                        cupos de {franja.slotDurationMinutes} min
                        {franja.maxConcurrent > 1 ? ` · ${franja.maxConcurrent} a la vez` : ''}
                      </span>
                    )}
                    {alto >= ALTO_MEDIA_HORA * 4 && franja.etiqueta && (
                      <span className="block truncate text-[var(--foreground-muted)]">
                        {franja.etiqueta}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
